import { useState, useMemo } from 'react';
import { Loader, Select as MantineSelect } from '@mantine/core';
import { Breadcrumbs } from '@components';
import { Button, Select, MultiSelect, DoubleCheckModal, RefetchButton } from '@shared/ui';
import { useProducts } from '@modules/products';
import { useCustomSets } from '@modules/custom-sets';
import { useExtras, useCreateExtra, useDeleteExtra, ExtraRuleTypeTo, ExtraRuleTypeFrom, type ExtraRule } from '@modules/extras';
import { toNumberOrNull } from '@shared/utils';
import '@shared/styles/page.css';
import './ExtrasPage.css';
import { IoAdd } from 'react-icons/io5';

type AddType = 'products' | 'sets';

export default function ExtrasPage() {
    const [selectedTargetIds, setSelectedTargetIds] = useState<number[]>([]);
    const [selectedAddType, setSelectedAddType] = useState<AddType | null>(null);
    const [selectedAddIds, setSelectedAddIds] = useState<number[]>([]);
    const [selectedSetId, setSelectedSetId] = useState<number | null>(null);

    const [ruleToDelete, setRuleToDelete] = useState<number | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const { data: products = [], isLoading: productsLoading } = useProducts();
    const { data: customSets = [], isLoading: customSetsLoading } = useCustomSets();
    const { data: rules = [], isLoading: rulesLoading, error, refetch, isFetching: rulesFetching } = useExtras();
    const createExtra = useCreateExtra();
    const deleteExtra = useDeleteExtra();

    const productOptions = useMemo(() => {
        return products.map(product => {
            const categoryNames = product.categories
                ?.map(cat => cat.category.name)
                .filter(Boolean)
                .join(', ') || '';
            const label = categoryNames
                ? `ID: ${product.id} ${product.name} (${categoryNames})`
                : `ID: ${product.id} ${product.name}`;
            return { value: product.id.toString(), label };
        });
    }, [products]);

    const addTypeOptions = [
        { value: 'products', label: 'Добавить товары' },
        { value: 'sets', label: 'Добавить подборку' },
    ];

    const addOptions = useMemo(() => {
        if (selectedAddType === 'products') {
            return productOptions;
        }
        if (selectedAddType === 'sets') {
            return customSets.map(set => ({
                value: set.id.toString(),
                label: set.name,
            }));
        }
        return [];
    }, [selectedAddType, productOptions, customSets]);

    const isLoading =
        productsLoading ||
        customSetsLoading ||
        rulesLoading ||
        createExtra.isPending ||
        deleteExtra.isPending;

    const handleAddRule = () => {
        if (selectedTargetIds.length === 0 || !selectedAddType) {
            return;
        }

        if (selectedAddType === 'products') {
            if (selectedAddIds.length === 0) return;
        } else if (!selectedSetId) {
            return;
        }

        createExtra.mutate({
            typeTo: ExtraRuleTypeTo.PRODUCTS,
            idsTo: selectedTargetIds,
            typeFrom: selectedAddType === 'products'
                ? ExtraRuleTypeFrom.PRODUCTS
                : ExtraRuleTypeFrom.SET,
            idsFrom: selectedAddType === 'products'
                ? selectedAddIds
                : [selectedSetId!],
        });

        setSelectedTargetIds([]);
        setSelectedAddType(null);
        setSelectedAddIds([]);
        setSelectedSetId(null);
    };

    const handleDeleteRule = (ruleId: number) => {
        setRuleToDelete(ruleId);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteRule = async () => {
        if (ruleToDelete !== null) {
            try {
                await deleteExtra.mutateAsync(ruleToDelete);
                setIsDeleteModalOpen(false);
                setRuleToDelete(null);
            } catch {
                // Ошибка обрабатывается в хуке через onError
            }
        }
    };

    const cancelDeleteRule = () => {
        setIsDeleteModalOpen(false);
        setRuleToDelete(null);
    };

    const getRuleDescription = (rule: ExtraRule): string => {
        if (rule.typeTo !== ExtraRuleTypeTo.PRODUCTS) {
            return `Устаревшее правило (${rule.typeTo}) — удалите`;
        }

        const productNames = rule.idsTo
            .map(id => products.find(p => p.id === id)?.name)
            .filter(Boolean)
            .join(', ') || '';
        const targetDesc = productNames ? `товарам: ${productNames}` : 'товарам';

        let addDesc = '';
        if (rule.typeFrom === ExtraRuleTypeFrom.PRODUCTS) {
            const names = rule.idsFrom
                .map(id => products.find(p => p.id === id)?.name)
                .filter(Boolean)
                .join(', ') || '';
            addDesc = `добавить товары: ${names}`;
        } else if (rule.typeFrom === ExtraRuleTypeFrom.SET) {
            const setId = rule.idsFrom[0];
            const set = customSets.find(s => s.id === setId);
            addDesc = set ? `добавить подборку: ${set.name}` : 'добавить подборку';
        }

        return `Для ${targetDesc} — ${addDesc}`;
    };

    const productRules = useMemo(
        () => rules.filter((r) => r.typeTo === ExtraRuleTypeTo.PRODUCTS),
        [rules],
    );
    const legacyRules = useMemo(
        () => rules.filter((r) => r.typeTo !== ExtraRuleTypeTo.PRODUCTS),
        [rules],
    );

    const getContent = () => {
        if (rulesLoading || rulesFetching) {
            return (
                <div className="empty-container">
                    <Loader />
                </div>
            );
        }

        if (error && refetch) {
            return (
                <RefetchButton
                    refetch={refetch}
                    isLoading={rulesLoading || rulesFetching}
                />
            );
        }

        if (rules.length === 0) {
            return <div className="empty-container">
                <p>Нет правил. Создайте первое правило выше или на карточке товара.</p>
            </div>
        }

        return <div className="extras-rules-grid">
            {[...productRules, ...legacyRules].map(rule => (
                <div key={rule.id} className="extras-rule-card">
                    <div className="extras-rule-content">
                        <p>{getRuleDescription(rule)}</p>
                    </div>
                    <div className="extras-rule-actions">
                        <Button
                            variant="secondary"
                            onClick={() => handleDeleteRule(rule.id)}
                            style={{ color: '#d32f2f', borderColor: '#d32f2f' }}
                        >
                            Удалить
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    }

    const addDisabled =
        selectedTargetIds.length === 0 ||
        !selectedAddType ||
        (selectedAddType === 'products' ? selectedAddIds.length === 0 : selectedSetId === null) ||
        isLoading;

    return (
        <div className="page-container">
            <Breadcrumbs items={[
                { label: 'Главная', path: '/' },
                { label: 'Допы' }
            ]} />

            <div className="page-header">
                <div className="page-header-top">
                    <h1>Допы</h1>
                </div>
            </div>

            <div className="extras-create-block">
                <div className="extras-rules-form">
                    <h3 style={{ marginBottom: '20px' }}>Создать правило</h3>

                    <div className="extras-form-grid">
                        <div className="extras-form-field">
                            <MultiSelect
                                label="Для товаров"
                                placeholder={
                                    products.length === 0
                                        ? 'Нет товаров'
                                        : 'Выберите товары...'
                                }
                                data={productOptions}
                                value={selectedTargetIds}
                                onChange={(values) => setSelectedTargetIds(values as number[])}
                                searchable
                                disabled={products.length === 0}
                            />
                        </div>

                        {selectedTargetIds.length > 0 && (
                            <div className="extras-form-field">
                                <MantineSelect
                                    label="Добавить"
                                    placeholder="Выберите тип"
                                    data={addTypeOptions}
                                    value={selectedAddType}
                                    onChange={(value) => {
                                        setSelectedAddType(value as AddType | null);
                                        setSelectedAddIds([]);
                                        setSelectedSetId(null);
                                    }}
                                    searchable
                                />
                            </div>
                        )}

                        {selectedAddType && (
                            <div className="extras-form-field">
                                {selectedAddType === 'products' ? (
                                    <MultiSelect
                                        label="Выберите товары"
                                        placeholder="Выберите..."
                                        data={addOptions}
                                        value={selectedAddIds}
                                        onChange={(values) => setSelectedAddIds(values as number[])}
                                        searchable
                                    />
                                ) : (
                                    <Select
                                        label="Выберите подборку"
                                        placeholder="Выберите..."
                                        data={addOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                                        value={selectedSetId}
                                        onChange={(value) => setSelectedSetId(toNumberOrNull(value))}
                                        searchable
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    <div className="extras-form-actions">
                        <Button
                            variant="primary"
                            leftAddon={<IoAdd size={20} />}
                            onClick={handleAddRule}
                            loading={createExtra.isPending}
                            disabled={addDisabled}
                        >
                            Добавить правило
                        </Button>
                    </div>
                </div>

                <div className="extras-rules-list">
                    <h2>Правила</h2>

                    {getContent()}
                </div>
            </div>

            <DoubleCheckModal
                isOpen={isDeleteModalOpen}
                onClose={cancelDeleteRule}
                title="Удалить правило"
                message="Вы уверены, что хотите удалить это правило? Это действие нельзя отменить."
                secondButtonText="Удалить"
                secondButtonAction={confirmDeleteRule}
                isLoading={deleteExtra.isPending}
            />
        </div>
    );
}
